import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar, Clock, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const { t } = useLanguage();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [leadId]);

  const fetchData = async () => {
    try {
      const [slotsRes, leadRes] = await Promise.all([
        axios.get(`${API}/booking/slots`),
        leadId ? axios.get(`${API}/diagnosis/results/${leadId}`) : Promise.resolve(null)
      ]);
      setSlots(slotsRes.data);
      if (leadRes?.data) {
        setLeadData(leadRes.data);
        setFormData(prev => ({
          ...prev,
          name: leadRes.data.name || '',
          email: leadRes.data.email || '',
          phone: leadRes.data.phone || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) {
      setError('Please select a time slot');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/booking/book`, {
        slot_id: selectedSlot.id,
        lead_id: leadId || 'direct-booking',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to book. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Group slots by date
  const slotsByDate = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  if (success) {
    return (
      <div className="pt-20 min-h-screen bg-gray-50 flex items-center justify-center" data-testid="booking-success">
        <div className="bg-white p-12 max-w-md text-center border border-gray-200">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] mb-4">Booking Confirmed!</h1>
          <p className="text-gray-600 mb-2">
            Your discovery call has been scheduled for:
          </p>
          <p className="text-lg font-semibold text-[#1A1A1A] mb-6">
            {formatDate(selectedSlot.date)} at {selectedSlot.time}
          </p>
          <p className="text-sm text-gray-500 mb-8">
            A confirmation email has been sent to {formData.email}
          </p>
          <Button onClick={() => navigate('/')} className="btn-primary">
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-gray-50" data-testid="booking-page">
      {/* Header */}
      <section className="py-12 bg-[#1A1A1A] text-white">
        <div className="container-custom">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <p className="text-[#C5A059] uppercase tracking-widest text-sm font-medium mb-4">
            {t('contact.booking')}
          </p>
          <h1 className="text-4xl md:text-5xl font-bold">
            Book a Discovery Call
          </h1>
          <p className="text-xl text-gray-300 mt-4 max-w-2xl">
            Schedule a free 30-minute call with our team to discuss your results and explore how TeamCraft can help.
          </p>
        </div>
      </section>

      <div className="container-custom py-12">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Calendar Section */}
            <div className="lg:col-span-7 space-y-6" data-testid="slot-selection">
              <h2 className="text-2xl font-semibold text-[#1A1A1A] flex items-center gap-3">
                <Calendar className="w-6 h-6 text-[#C5A059]" />
                {t('contact.selectSlot')}
              </h2>

              <div className="space-y-6">
                {Object.entries(slotsByDate).slice(0, 7).map(([date, dateSlots]) => (
                  <div key={date} className="bg-white p-6 border border-gray-200">
                    <h3 className="font-semibold text-[#1A1A1A] mb-4">{formatDate(date)}</h3>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                      {dateSlots.map((slot) => (
                        <button
                          key={slot.id}
                          onClick={() => slot.is_available && setSelectedSlot(slot)}
                          disabled={!slot.is_available}
                          className={`p-3 border text-center transition-all ${
                            selectedSlot?.id === slot.id
                              ? 'border-[#C5A059] bg-[#C5A059]/10'
                              : slot.is_available
                              ? 'border-gray-200 hover:border-[#C5A059]'
                              : 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                          }`}
                          data-testid={`slot-${slot.id}`}
                        >
                          <Clock className={`w-4 h-4 mx-auto mb-1 ${selectedSlot?.id === slot.id ? 'text-[#C5A059]' : ''}`} />
                          <span className="text-sm font-mono">{slot.time}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Section */}
            <div className="lg:col-span-5">
              <div className="bg-white p-8 border border-gray-200 sticky top-24">
                <h2 className="text-xl font-semibold text-[#1A1A1A] mb-6">Your Information</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('contact.name')} *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      data-testid="booking-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t('contact.email')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      data-testid="booking-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('contact.phone')}</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      data-testid="booking-phone"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any specific topics you'd like to discuss?"
                      rows={3}
                      data-testid="booking-notes"
                    />
                  </div>

                  {selectedSlot && (
                    <div className="p-4 bg-[#C5A059]/10 border border-[#C5A059]/30">
                      <p className="text-sm font-medium text-[#1A1A1A]">Selected Time:</p>
                      <p className="text-lg font-semibold text-[#1A1A1A]">
                        {formatDate(selectedSlot.date)} at {selectedSlot.time}
                      </p>
                    </div>
                  )}

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={!selectedSlot || submitting}
                    className="w-full btn-bronze"
                    data-testid="confirm-booking-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Confirming...
                      </>
                    ) : (
                      'Confirm Booking'
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
